import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

type ProviderImageProps = {
  imageUrl: string | null;
  name: string;
  isImageLoading: boolean;
  shouldLoadImage: boolean;
  providerId?: string;
  onImageLoad: () => void;
  onImageError: () => void;
  onShouldLoadChange: (shouldLoad: boolean) => void;
};

export const ProviderImage = ({
  imageUrl,
  name,
  isImageLoading,
  shouldLoadImage,
  providerId,
  onImageLoad,
  onImageError,
  onShouldLoadChange,
}: ProviderImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleImageClick = () => {
    if (providerId) {
      navigate(`/providers/${providerId}`);
    }
  };

  // Use Intersection Observer to trigger image loading when in viewport
  useEffect(() => {
    if (!containerRef.current || shouldLoadImage || !imageUrl) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onShouldLoadChange(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [imageUrl, shouldLoadImage, onShouldLoadChange]);

  return (
    <div
      ref={containerRef}
      className={`w-full max-w-[200px] sm:max-w-[240px] lg:max-w-full rounded-2xl border border-[#E5E7EB] overflow-hidden shadow-sm bg-white relative ${providerId ? 'cursor-pointer hover:border-[#026857] transition-colors' : ''}`}
      onClick={handleImageClick}
      role={providerId ? "button" : undefined}
      tabIndex={providerId ? 0 : undefined}
      onKeyDown={providerId ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.key === ' ') {
            e.preventDefault();
          }
          handleImageClick();
        }
      } : undefined}
    >
      {/* Show spinner only when image is actively loading */}
      {isImageLoading && shouldLoadImage && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E5E7EB] border-t-[#026857]"></div>
        </div>
      )}
      {/* Show spinner placeholder when image URL exists but hasn't started loading yet */}
      {imageUrl && !shouldLoadImage && (
        <div className="w-full aspect-square bg-[#F3F4F6] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E5E7EB] border-t-[#026857]"></div>
        </div>
      )}
      {/* Render image only when shouldLoadImage is true */}
      {imageUrl && shouldLoadImage && (
        <img
          src={imageUrl}
          alt={name}
          className={`w-full aspect-square object-contain bg-white ${isImageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          loading="lazy"
          onLoad={onImageLoad}
          onError={onImageError}
        />
      )}
      {/* No image available state */}
      {!imageUrl && (
        <div className="w-full aspect-square bg-[#F3F4F6] flex items-center justify-center">
          <span className="text-sm font-termina text-[#6B7280]">No image available</span>
        </div>
      )}
    </div>
  );
};

